/**
 * A journey with a position in it: where a programme has reached, and what is left.
 *
 * The governance panel drew this with hand-written markers and literal hex
 * values, one of them off-brand. The states are generic, so this is the
 * component rather than that markup, on daisyUI's `steps`.
 *
 * @module components/ui/Stepper
 */

import React from "react";

import { Icon } from "./Icon";

export const STEP_STATES = ["done", "current", "blocked", "ended", "pending", "skipped"] as const;
export type StepState = (typeof STEP_STATES)[number];

export interface StepperStep {
  key: string;
  label: string;
  /** Detail beside the state, e.g. "Signed off 12 March". The state itself is
   * always announced, so a hint repeating it is read twice. */
  hint?: string;
  state: StepState;
  "data-testid"?: string;
}

export interface StepperProps {
  steps: readonly StepperStep[];
  /** Names the journey, since the steps alone do not say what they track. */
  "aria-label": string;
  className?: string;
  "data-testid"?: string;
}

/*
 * What each state is called. The markers are `aria-hidden` and the colours say
 * nothing to a screen reader, so without a word here a blocked step read
 * exactly like a pending one.
 */
const STATE_LABEL: Record<StepState, string> = {
  done: "Complete",
  current: "In progress",
  blocked: "Changes required",
  ended: "Ended here",
  pending: "Not started",
  skipped: "Skipped",
};

/** Whole class names, so Tailwind finds them without a safelist line. */
const STEP_STATE_CLASS: Record<StepState, string> = {
  // Soft: a settled step is never the loudest mark on the page.
  done: "step-success before:[--step-bg:var(--color-success-bg)]",
  current: "step-primary",
  blocked: "step-error",
  ended: "step-neutral",
  pending: "",
  skipped: "",
};

/** Marker colours over daisyUI's: soft for done, pale for the bare number. */
const STEP_ICON_CLASS: Record<StepState, string> = {
  done: "[--step-bg:var(--color-success-bg)] [--step-fg:var(--color-success-strong)]",
  current: "",
  blocked: "",
  ended: "",
  pending: "[--step-bg:var(--color-base-200)] [--step-fg:var(--color-muted)]",
  skipped: "[--step-bg:var(--color-base-200)] [--step-fg:var(--color-muted)]",
};

const STATE_ICON: Record<StepState, string | null> = {
  done: "check",
  current: "clock",
  blocked: "x",
  ended: "archive",
  pending: null,
  skipped: "skip-forward",
};

/**
 * @example
 * ```tsx
 * <Stepper aria-label="Approval journey" steps={stations} />
 * ```
 */
export function Stepper({
  steps,
  "aria-label": label,
  className = "",
  "data-testid": testId,
}: StepperProps): React.ReactElement {
  return (
    // A column until its own box is wide enough for the row: a phone showed
    // three stations and clipped the fourth, with nothing to say more followed.
    <div className="@container">
      <ol
        aria-label={label}
        className={`steps steps-vertical @3xl:steps-horizontal w-full pb-2 ${className}`.trim()}
        data-testid={testId}
      >
        {steps.map((step, index) => {
          const icon = STATE_ICON[step.state];
          return (
            <li
              key={step.key}
              className={["step", STEP_STATE_CLASS[step.state]].filter(Boolean).join(" ")}
              // The one the reader is being told about, so a screen reader can
              // find the position without counting the marks.
              aria-current={step.state === "current" ? "step" : undefined}
              data-testid={step["data-testid"]}
            >
              {/* The number is the fallback, so a pending step still says which
                one it is rather than sitting blank. Pale, since daisyUI paints
                a bare marker in Ink. */}
              <span className={`step-icon text-sm font-bold ${STEP_ICON_CLASS[step.state]}`}>
                {icon ? <Icon name={icon} aria-hidden /> : index + 1}
              </span>
              {/* Positioned so the absolute `sr-only` state stays inside a
                caller's scroll container instead of widening the page. */}
              <span className="relative flex min-w-[7rem] flex-col px-1 text-start @3xl:text-center">
                <span className="text-xs font-semibold">{step.label}</span>
                <span className="sr-only">{STATE_LABEL[step.state]}</span>
                {step.hint && <span className="text-xs text-muted">{step.hint}</span>}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
